import type { GrammarError, AIComment, CorrectionSuggestion } from '$lib/types/grammar';
import { GrammarErrorFactory } from './GrammarErrorFactory';

/**
 * Service to merge grammar check errors with AI analysis results
 * Handles the combination of Bareun API results and AI-only findings
 */
export class ErrorMergeService {
  /**
   * Merge grammar errors with AI comments
   * @param grammarErrors Errors from grammar check (Bareun API)
   * @param aiComments Map of error ID to AI comments
   * @param aiOnlyErrors Errors found only by AI analysis
   * @returns Merged array of errors
   */
  static mergeErrors(
    grammarErrors: GrammarError[],
    aiComments: Map<string, AIComment>,
    aiOnlyErrors: GrammarError[] = []
  ): GrammarError[] {
    // Normalize all errors to ensure unified format
    const normalizedGrammarErrors = grammarErrors.map(error => 
      GrammarErrorFactory.normalizeExisting(error)
    );
    const normalizedAIErrors = aiOnlyErrors.map(error => 
      GrammarErrorFactory.normalizeExisting({ ...error, isAIOnly: true })
    );

    // Add AI comments to existing grammar errors
    const enhancedGrammarErrors = normalizedGrammarErrors.map(error => {
      const aiComment = aiComments.get(error.id);
      if (aiComment) {
        return {
          ...error,
          aiComment
        };
      }
      return error;
    });

    // Combine with AI-only errors (marked appropriately)
    const aiOnlyWithFlag = normalizedAIErrors.map(error => ({
      ...error,
      isAIOnly: true,
      aiComment: aiComments.get(error.id)
    }));

    // Merge and sort by position
    const allErrors = [...enhancedGrammarErrors, ...aiOnlyWithFlag];
    
    // Sort by position in text
    allErrors.sort((a, b) => {
      if (a.position && b.position) {
        return a.position.start - b.position.start;
      }
      return 0;
    });

    return allErrors;
  }

  /**
   * Find errors that might be duplicates between grammar check and AI
   * @param existingErrors Existing errors (could be AI or grammar)
   * @param newErrors New errors to merge (could be AI or grammar)
   * @returns Merged errors with combined descriptions for overlapping positions
   */
  static deduplicateErrors(
    existingErrors: GrammarError[],
    newErrors: GrammarError[]
  ): GrammarError[] {
    
    const merged: GrammarError[] = [];
    const processedExistingErrors = new Set<string>();
    
    // Process new errors and merge with overlapping existing errors
    for (const newError of newErrors) {
      
      // Find overlapping existing error
      const overlappingExisting = existingErrors.find(existingError => {
        // Get normalized text for comparison
        const newText = (newError.original || newError.originalText || '').trim();
        const existingText = (existingError.original || existingError.originalText || '').trim();
        
        // Get normalized positions
        const newStart = newError.position?.start ?? (newError as any).startPos;
        const newEnd = newError.position?.end ?? (newError as any).endPos;
        const existingStart = existingError.position?.start ?? (existingError as any).startPos;
        const existingEnd = existingError.position?.end ?? (existingError as any).endPos;
        
        // Same text = duplicate (regardless of position, since positions can be inaccurate)
        if (newText && existingText && newText === existingText) {
          return true;
        }
        
        // Check exact position overlap (for different text at same position)
        if (newStart !== undefined && newEnd !== undefined && 
            existingStart !== undefined && existingEnd !== undefined) {
          const overlap = 
            (existingStart >= newStart && existingStart < newEnd) ||
            (existingEnd > newStart && existingEnd <= newEnd) ||
            (existingStart <= newStart && existingEnd >= newEnd);
          
          if (overlap) {
            return true;
          }
        }
        
        return false;
      });

      if (overlappingExisting) {
        // Mark this existing error as processed
        processedExistingErrors.add(overlappingExisting.id);

        // Merge the two errors - prefer the more accurate position (usually grammar check)
        const mergedError = this.mergeOverlappingErrors(overlappingExisting, newError);
        merged.push(mergedError);
      } else {
        // No overlap, add new error as is, preserving user action properties
        merged.push(GrammarErrorFactory.normalizeExisting({
          ...newError,
          // Preserve user action properties if they exist
          isOriginalKept: newError.isOriginalKept,
          userAction: newError.userAction
        }));
      }
    }
    
    // Add existing errors that weren't overlapping with any new error
    for (const existingError of existingErrors) {
      if (!processedExistingErrors.has(existingError.id)) {
        merged.push(
          GrammarErrorFactory.normalizeExisting({
            ...existingError,
            isAIOnly: existingError.isAIOnly,
            isAIGenerated: existingError.isAIGenerated,
            // IMPORTANT: Preserve user action properties
            isOriginalKept: existingError.isOriginalKept,
            userAction: existingError.userAction
          })
        );
      }
    }

    // Sort by position
    merged.sort((a, b) => {
      if (a.position && b.position) {
        return a.position.start - b.position.start;
      }
      return 0;
    });

    return merged;
  }

  /**
   * Merge two overlapping errors, combining their descriptions
   * @param existingError The existing error (AI or grammar)
   * @param newError The new error (AI or grammar)
   * @returns Merged error with combined information
   */
  private static mergeOverlappingErrors(
    existingError: GrammarError,
    newError: GrammarError
  ): GrammarError {
    // Determine which error is AI and which is grammar
    const aiError = existingError.isAIGenerated ? existingError : newError.isAIGenerated ? newError : null;
    const grammarError = !existingError.isAIGenerated ? existingError : !newError.isAIGenerated ? newError : null;
    
    // IMPORTANT: Use grammar error as base if available (it has more accurate positions)
    // Otherwise use the non-AI error, and lastly the new error
    const baseError = grammarError || (!existingError.isAIGenerated ? existingError : newError);
    
    // Merge corrections from both sources
    const mergedCorrections: CorrectionSuggestion[] = [];
    const correctionMap = new Map<string, CorrectionSuggestion>();
    
    // Add grammar corrections first (higher priority)
    if (grammarError?.corrections) {
      grammarError.corrections.forEach(c => {
        correctionMap.set(c.text, c);
      });
    }
    
    // Add AI corrections if different
    if (aiError?.corrections) {
      aiError.corrections.forEach(c => {
        if (!correctionMap.has(c.text)) {
          correctionMap.set(c.text, c);
        }
      });
    }
    
    // Convert map to array
    mergedCorrections.push(...correctionMap.values());
    
    // Combine AI comment information
    let aiComment: AIComment | undefined;
    if (aiError?.aiComment) {
      aiComment = aiError.aiComment;
    } else if (existingError.isAIGenerated || newError.isAIGenerated) {
      // Create AI comment from AI error information
      const aiSource = existingError.isAIGenerated ? existingError : newError;
      aiComment = {
        text: aiSource.message || aiSource.help || '',
        confidence: aiSource.confidence || 0.8,
        agreesWithCorrection: true,
        timestamp: Date.now()
      };
    }
    
    // Create merged error with both grammar and AI information
    return GrammarErrorFactory.normalizeExisting({
      ...baseError,
      corrections: mergedCorrections.length > 0 ? mergedCorrections : baseError.corrections,
      aiComment,
      // Set flags to indicate this error has both checks
      isAIGenerated: false, // Not just AI-generated
      isAIOnly: false, // Not AI-only since we have both
      hasBothChecks: true, // New flag to indicate both checks were performed
      // Preserve grammar check help text
      help: grammarError?.help || baseError.help,
      message: grammarError?.message || baseError.message,
      // Keep the best suggestions array for compatibility
      suggestions: mergedCorrections.map(c => c.text),
      // IMPORTANT: Preserve user action properties from existing error
      isOriginalKept: existingError.isOriginalKept || newError.isOriginalKept,
      userAction: existingError.userAction || newError.userAction
    });
  }

  /**
   * Update error states based on user actions
   * @param errors Current errors
   * @param errorId Error ID to update
   * @param action User action
   * @returns Updated errors
   */
  static updateErrorAction(
    errors: GrammarError[],
    errorId: string,
    action: 'kept-original' | 'accepted' | 'rejected' | 'edited' | 'ignored'
  ): GrammarError[] {
    return errors.map(error => {
      if (error.id === errorId) {
        return {
          ...error,
          userAction: action,
          isOriginalKept: action === 'kept-original',
          isResolved: action === 'accepted' || action === 'edited'
        };
      }
      return error;
    });
  }

  /**
   * Generate a unique error ID
   * @param text Error text
   * @param position Error position
   * @returns Unique ID
   */
  static generateErrorId(text: string, position: { start: number; end: number }): string {
    return `error-${position.start}-${position.end}-${text.substring(0, 10)}`;
  }

  /**
   * Check if an error has uncertain AI analysis
   * @param error Grammar error
   * @returns Whether the AI analysis is uncertain
   */
  static isUncertainError(error: GrammarError): boolean {
    return error.aiComment?.isUncertain === true || 
           (error.aiComment?.confidence ?? 1) < 0.6;
  }

  /**
   * Filter errors by type and AI agreement
   * @param errors All errors
   * @param options Filter options
   * @returns Filtered errors
   */
  static filterErrors(
    errors: GrammarError[],
    options: {
      includeGrammarOnly?: boolean;
      includeAIOnly?: boolean;
      includeDisagreements?: boolean;
      includeUncertain?: boolean;
    } = {}
  ): GrammarError[] {
    const {
      includeGrammarOnly = true,
      includeAIOnly = true,
      includeDisagreements = true,
      includeUncertain = true
    } = options;

    return errors.filter(error => {
      // Filter by source
      if (!includeGrammarOnly && !error.isAIOnly) return false;
      if (!includeAIOnly && error.isAIOnly) return false;

      // Filter by AI agreement
      if (!includeDisagreements && error.aiComment && !error.aiComment.agreesWithCorrection) {
        return false;
      }

      // Filter by uncertainty
      if (!includeUncertain && this.isUncertainError(error)) {
        return false;
      }

      return true;
    });
  }
}